import React, { useEffect } from "react";
import { css } from "@emotion/react";
import { GOOGLE_API_KEY } from "main/javascripts/constants/Constants";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { Cruise } from "main/javascripts/types/cruise";
import { accentColor } from "main/javascripts/styles/base/colorStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";

export interface IProps {
  cruiseRoutes: Cruise.CruiseRoute[];
}

export const CruiseRouteListMap: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cruiseRoutes } = props;

  const [scale, setScale] = React.useState(1);

  useEffect(() => {
    setScale(window.devicePixelRatio > 1 ? 2 : 1);
  }, []);

  const getMiddlePosition = (prev: any, next: any) => {
    if (prev && next) {
      const lat1 = Number(prev.lat);
      const lon1_original = Number(prev.lng);
      const lat2 = Number(next.lat);
      const lon2_original = Number(next.lng);

      // 経度が180度以上離れている場合、一方を±360度シフト
      let lon1 = lon1_original;
      let lon2 = lon2_original;
      if (Math.abs(lon1 - lon2) > 180) {
        if (lon1 > lon2) {
          lon2 += 360;
        } else {
          lon1 += 360;
        }
      }

      const latitude = (lat1 + lat2) / 2;
      let midpointLongitude = (lon1 + lon2) / 2;
      if (midpointLongitude > 180) {
        midpointLongitude -= 360;
      } else if (midpointLongitude <= -180) {
        midpointLongitude += 360;
      }

      return {
        lat: latitude,
        lng: midpointLongitude,
      };
    }
    return null;
  };

  const findNearestPosition = (routes: any[], currentIndex: number) => {
    const prev = routes
      .slice(0, currentIndex)
      .reverse()
      .find((route) => route?.port?.latitude && route?.port?.longitude);

    const next = routes
      .slice(currentIndex + 1)
      .find((route) => route?.port?.latitude && route?.port?.longitude);

    return getMiddlePosition(
      prev
        ? { lat: Number(prev.port.latitude), lng: Number(prev.port.longitude) }
        : null,
      next
        ? { lat: Number(next.port.latitude), lng: Number(next.port.longitude) }
        : null
    );
  };

  const markerData = (() => {
    const positionCountMap: Record<string, number> = {};
    return cruiseRoutes.map((cruiseRoute, index) => {
      let position = null;
      const isInvalid =
        cruiseRoute.routeType === "sailing" ||
        !cruiseRoute.port ||
        !cruiseRoute.port.latitude ||
        !cruiseRoute.port.longitude;

      if (!isInvalid) {
        position = {
          lat: Number(cruiseRoute.port.latitude),
          lng: Number(cruiseRoute.port.longitude),
        };
      } else {
        position = findNearestPosition(cruiseRoutes, index);
      }

      const positionKey = position ? `${position.lat},${position.lng}` : null;
      const duplicateCount = positionKey
        ? positionCountMap[positionKey] || 0
        : 0;
      if (positionKey) positionCountMap[positionKey] = duplicateCount + 1;

      return {
        day: cruiseRoute.day,
        title: cruiseRoute.title,
        position: position,
        duplicateCount: duplicateCount,
      };
    });
  })();

  const coordinates = markerData
    .filter((data) => data.position !== null)
    .map((data) => data.position);
  const filteredMarkerData = markerData.filter(
    (data) => data.position !== null
  );

  // 経度を中心（center）に対してシフトする関数
  const shiftLongitude = (lon: number, center: number) => {
    let shifted = lon - center;
    shifted = ((shifted + 180) % 360) - 180;
    return shifted;
  };

  const calculateZoom = (locations: { lat: number; lng: number }[]) => {
    const latitudes = locations.map((loc) => loc.lat);
    const longitudes = locations.map((loc) => loc.lng);
    const avgLon = longitudes.reduce((a, b) => a + b, 0) / longitudes.length;
    const shiftedLongitudes = longitudes.map((lon) =>
      shiftLongitude(lon, avgLon)
    );

    const maxLon = Math.max(...shiftedLongitudes);
    const minLon = Math.min(...shiftedLongitudes);
    const maxLat = Math.max(...latitudes);
    const minLat = Math.min(...latitudes);

    const latDiff = maxLat - minLat;
    const lonDiff = maxLon - minLon;
    const maxDiff = Math.max(latDiff * 2, lonDiff);
    if (maxDiff > 200) return 1;
    if (maxDiff > 100) return 2;
    if (maxDiff > 40) return 3;
    if (maxDiff > 15) return 4;
    if (maxDiff > 7) return 5;
    if (maxDiff > 4) return 6;
    if (maxDiff > 2) return 7;
    return 8;
  };

  function calculateCenter(coordinates: { lat: number; lng: number }[]) {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const toDeg = (rad: number) => (rad * 180) / Math.PI;
    let x = 0,
      y = 0,
      z = 0;

    coordinates.forEach((coord) => {
      const lat = toRad(coord.lat);
      const lon = toRad(coord.lng);
      x += Math.cos(lat) * Math.cos(lon);
      y += Math.cos(lat) * Math.sin(lon);
      z += Math.sin(lat);
    });

    const total = coordinates.length;
    x /= total;
    y /= total;
    z /= total;

    const hyp = Math.sqrt(x * x + y * y);
    const centerLat = Math.atan2(z, hyp);
    const centerLon = Math.atan2(y, x);

    return {
      lat: toDeg(centerLat),
      lng: toDeg(centerLon),
    };
  }

  const zoom = calculateZoom(coordinates);
  const center = calculateCenter(coordinates);

  // Static Maps 用の URL を生成
  // ※サイズは例として600x288(px)（CSSの高さ 18rem ≒ 288px を参考）としています
  const mapWidth = 600;
  const mapHeight = 288;

  const pinColor = accentColor.primaryColor.replace("#", "0x");

  // 各マーカーについて、パラメータ（例：ラベルは day の下1桁）を作成
  const markersParams = filteredMarkerData
    .map(
      (data) =>
        `markers=label:${String(data.day % 10)}|color:${pinColor}|${
          data.position.lat
        },${data.position.lng}`
    )
    .join("&");

  // ルートのラインを示すパスパラメータ（各座標をパイプ(|)で区切り）
  const validCoordinates = coordinates.filter((coord) => coord);
  const pathParam = validCoordinates.length
    ? `path=weight:3|color:0x000000|${validCoordinates
        .map((coord) => `${coord.lat},${coord.lng}`)
        .join("|")}`
    : "";

  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${
    center.lat
  },${
    center.lng
  }&zoom=${zoom}&size=${mapWidth}x${mapHeight}&scale=${scale}&map_id=ca303613a46cfe5a&${markersParams}${
    pathParam ? "&" + pathParam : ""
  }&key=${GOOGLE_API_KEY}`;

  return (
    <div css={blockStyle}>
      <img src={staticMapUrl} alt="Cruise Route Map" css={mapStyle} />
    </div>
  );
};

const blockStyle = css`
  width: auto;
  height: 18rem;
  border-radius: ${borderRadius.normal};
  overflow: hidden;
  position: relative;
  outline: none;
  ${moreThanBreakpoint("tablet")} {
    height: 20rem;
  }
`;
const mapStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
